import React, { useCallback, useMemo } from 'react';
import {
    Badge,
    Box,
    Flex,
    Grid,
    Heading,
    HStack,
    Icon,
    Progress,
    SimpleGrid,
    Table,
    Tbody,
    Td,
    Text,
    Th,
    Thead,
    Tr,
    useColorMode,
    VStack,
} from '@chakra-ui/react';
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    PieChart,
    Pie,
    Cell,
    Legend,
} from 'recharts';
import {
    AiOutlineCalendar,
    AiOutlinePieChart,
} from 'react-icons/ai';
import { useComercial, ETAPAS_PIPELINE } from '../../../../contexts/ComercialContext';
import { Panel, KpiCard } from '../../shared/Card';

const fmt = (v) =>
    new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 }).format(v || 0);

const CANAL_COLORS = ['#02EA75', '#4299E1', '#9F7AEA', '#ED8936', '#FC8181', '#ECC94B', '#38B2AC', '#4A5568'];

const ComercialDashboard = () => {
    const { colorMode } = useColorMode();
    const { negocios, atividades, indicadores, pipelines, canaisDropdown } = useComercial();

    // ── Etapas ativas do funil (dinâmicas conforme colunas do pipeline ativo) ──
    const etapasAtivas = useMemo(() => {
        const pipe = (pipelines || []).find(p => p.id === 'pipe-default') || (pipelines || [])[0];
        if (pipe?.colunas && pipe.colunas.length > 0) {
            return pipe.colunas
                .filter(col => col.tipo !== 'perdido' && String(col.label || '').toLowerCase() !== 'perdido')
                .map(col => ({
                    id: col.id,
                    label: col.label,
                    cor: col.cor || '#4299E1',
                    tipo: col.tipo || 'aberto',
                }));
        }
        return ETAPAS_PIPELINE.filter(e => e.id !== 'perdido');
    }, [pipelines]);

    // ── Todas as etapas para a tabela resumo (incluindo "Perdido") ─────────────
    const etapasTabela = useMemo(() => {
        const pipe = (pipelines || []).find(p => p.id === 'pipe-default') || (pipelines || [])[0];
        if (pipe?.colunas && pipe.colunas.length > 0) {
            return pipe.colunas.map(col => ({
                id: col.id,
                label: col.label,
                cor: col.cor || '#4299E1',
                tipo: col.tipo || 'aberto',
            }));
        }
        return ETAPAS_PIPELINE;
    }, [pipelines]);

    // Helper para verificar se o negócio pertence à etapa do funil
    const contaNoFunil = useCallback((n, etapaObj) => {
        const idMatch = n.colunaId === etapaObj.id || n.etapa === etapaObj.id;
        const tipoMatch = etapaObj.tipo === 'ganho' ? (n.etapa === 'ganho' || n.result_role === 1) : false;

        if (!idMatch && !tipoMatch) return false;
        if (etapaObj.tipo === 'ganho' || etapaObj.id === 'ganho' || etapaObj.tipo === 'perdido' || etapaObj.id === 'perdido') {
            return true;
        }
        return !n.completed;
    }, []);

    // ── Dados do Funil de Vendas para o BarChart ──────────────────────────────
    const funnelData = useMemo(() =>
        etapasAtivas.map(e => {
            const daEtapa = negocios.filter(n => contaNoFunil(n, e));
            return {
                etapaId: e.id,
                etapa: e.label, // Nome completo sem corte "..."
                total: daEtapa.length,
                valor: daEtapa.reduce((acc, n) => acc + (Number(n.valor) || 0), 0),
                cor: e.cor,
            };
        })
    , [etapasAtivas, negocios, contaNoFunil]);

    // ── Dados de Origem dos Negócios para o PieChart ──────────────────────────
    const canalData = useMemo(() => {
        const mapa = {};
        negocios.forEach(n => {
            let nomeCanal = String(n.canal || 'Outro').trim() || 'Outro';
            // Se o canal for id numérico, resolve o nome a partir do dropdown de canais
            if (canaisDropdown && canaisDropdown.length > 0) {
                const encontrado = canaisDropdown.find(c => String(c.id) === String(n.canal) || c.name === n.canal || c.label === n.canal);
                if (encontrado) nomeCanal = encontrado.label || encontrado.name || nomeCanal;
            }
            mapa[nomeCanal] = (mapa[nomeCanal] || 0) + 1;
        });

        return Object.entries(mapa)
            .map(([name, value]) => ({ name, value }))
            .sort((a, b) => b.value - a.value);
    }, [negocios, canaisDropdown]);

    // ── Fechamentos nos Próximos 14 dias ──────────────────────────────────────
    const proximosVencimentos = useMemo(() => {
        const hoje = new Date();
        const limite = new Date();
        limite.setDate(hoje.getDate() + 14);
        return negocios
            .filter(n => {
                if (n.completed) return false;
                if (n.etapa === 'ganho' || n.etapa === 'perdido') return false;
                if (!n.dataFechamento) return false;
                const d = new Date(`${String(n.dataFechamento).slice(0, 10)}T12:00:00`);
                return d >= hoje && d <= limite;
            })
            .sort((a, b) => new Date(a.dataFechamento) - new Date(b.dataFechamento))
            .slice(0, 6);
    }, [negocios]);

    // ── Atividades Pendentes ──────────────────────────────────────────────────
    const atividadesPendentes = useMemo(() =>
        atividades
            .filter(a => !a.completed && !a.canceled && (a.status_role === 0 || String(a.status || '').toLowerCase().includes('pendent')))
            .sort((a, b) => new Date(a.dataInicio || a.dataVencimento) - new Date(b.dataInicio || b.dataVencimento))
            .slice(0, 5)
    , [atividades]);

    const diasAte = (data) => {
        if (!data) return null;
        const diff = Math.ceil((new Date(`${String(data).slice(0, 10)}T12:00:00`) - new Date()) / (1000 * 60 * 60 * 24));
        return diff;
    };

    return (
        <VStack align="stretch" spacing={6}>
            <Flex justify="space-between" align="center" wrap="wrap" gap={3}>
                <Box>
                    <Heading size="lg" color="textPrimary" fontWeight="900">
                        Dashboard{' '}
                        <Text as="span" color="primary" bg="secondary" px={3} py={1} borderRadius="md">
                            Comercial
                        </Text>
                    </Heading>
                </Box>
                <Text fontSize="xs" color="textSecondary" fontWeight="600">
                    {new Date().toLocaleString('pt-BR')}
                </Text>
            </Flex>

            {/* KPIs */}
            <SimpleGrid columns={{ base: 2, md: 3, lg: 5 }} spacing={4}>
                <KpiCard label="Total de Negócios" value={indicadores.total} accent="#9F7AEA" />
                <KpiCard label="Em Aberto" value={indicadores.abertos} accent="#4299E1" />
                <KpiCard
                    label="Pipeline Total"
                    value={fmt(indicadores.totalPipeline)}
                    accent="#02EA75"
                />
                <KpiCard label="Negócios Ganhos" value={indicadores.ganhos} accent="#02EA75" />
                <Box
                    bg="surface"
                    borderRadius="xl"
                    p={5}
                    position="relative"
                    overflow="hidden"
                    border="1px solid"
                    borderColor="borderPrimary"
                    _before={{
                        content: '""',
                        position: 'absolute',
                        left: 0,
                        top: 0,
                        bottom: 0,
                        w: '4px',
                        bg: '#ED8936',
                    }}
                >
                    <Box fontSize="2.2rem" fontWeight="900" color="textPrimary" lineHeight="1">
                        {indicadores.taxaConversao}%
                    </Box>
                    <Box fontSize="0.65rem" fontWeight="700" textTransform="uppercase" color="textSecondary" mt={2} letterSpacing="1px">
                        Taxa de Conversão
                    </Box>
                    <Progress
                        value={indicadores.taxaConversao}
                        size="xs"
                        mt={2}
                        colorScheme="orange"
                        borderRadius="full"
                        bg={colorMode === 'dark' ? "gray.700" : "gray.200"}
                    />
                </Box>
            </SimpleGrid>

            {/* Gráficos Principais */}
            <Grid templateColumns={{ base: '1fr', lg: '2fr 1fr' }} gap={5}>
                {/* Gráfico de Barras: Funil de Vendas */}
                <Panel title="Funil de Vendas — Negócios por Etapa">
                    <Box h="300px" pt={2}>
                        {funnelData.length === 0 ? (
                            <Flex h="100%" align="center" justify="center">
                                <Text fontSize="sm" color="textMuted">Nenhuma etapa cadastrada no pipeline</Text>
                            </Flex>
                        ) : (
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart data={funnelData} margin={{ top: 15, right: 15, left: -15, bottom: 45 }}>
                                    <CartesianGrid stroke={colorMode === 'dark' ? "#2D3748" : "#E5E7EB"} strokeDasharray="3 3" />
                                    <XAxis
                                        dataKey="etapa"
                                        stroke={colorMode === 'dark' ? "#A0AEC0" : "#4B5563"}
                                        fontSize={10}
                                        fontWeight="700"
                                        interval={0}
                                        angle={-20}
                                        textAnchor="end"
                                        height={50}
                                    />
                                    <YAxis stroke={colorMode === 'dark' ? "#A0AEC0" : "#4B5563"} fontSize={11} allowDecimals={false} domain={[0, 'auto']} />
                                    <Tooltip
                                        content={({ active, payload }) => {
                                            if (active && payload && payload.length) {
                                                const data = payload[0].payload;
                                                return (
                                                    <Box
                                                        bg={colorMode === 'dark' ? '#1A202C' : '#FFFFFF'}
                                                        border="1px solid"
                                                        borderColor={colorMode === 'dark' ? '#2D3748' : '#E5E7EB'}
                                                        borderRadius="lg"
                                                        p={3}
                                                        boxShadow="xl"
                                                    >
                                                        <Text fontSize="xs" fontWeight="800" color="textPrimary" mb={1}>
                                                            {data.etapa}
                                                        </Text>
                                                        <HStack spacing={2} fontSize="xs">
                                                            <Box w="8px" h="8px" borderRadius="full" bg={data.cor} />
                                                            <Text color="textSecondary">Negócios:</Text>
                                                            <Text fontWeight="800" color="textPrimary">{data.total}</Text>
                                                        </HStack>
                                                        <HStack spacing={2} fontSize="xs" mt={0.5}>
                                                            <Text color="textSecondary" ml="10px">Valor total:</Text>
                                                            <Text fontWeight="800" color="secondary">{fmt(data.valor)}</Text>
                                                        </HStack>
                                                    </Box>
                                                );
                                            }
                                            return null;
                                        }}
                                    />
                                    <Bar dataKey="total" radius={[6, 6, 0, 0]}>
                                        {funnelData.map((entry, i) => (
                                            <Cell key={i} fill={entry.cor} />
                                        ))}
                                    </Bar>
                                </BarChart>
                            </ResponsiveContainer>
                        )}
                    </Box>
                </Panel>

                {/* Gráfico de Rosca: Origem dos Negócios */}
                <Panel title="Origem dos Negócios">
                    <Box h="300px" pt={2}>
                        {canalData.length === 0 ? (
                            <Flex h="100%" align="center" justify="center" direction="column" gap={2}>
                                <Icon as={AiOutlinePieChart} color="textMuted" boxSize="32px" />
                                <Text fontSize="sm" color="textMuted">Nenhuma origem de negócio registrada</Text>
                            </Flex>
                        ) : (
                            <ResponsiveContainer width="100%" height="100%">
                                <PieChart>
                                    <Pie
                                        data={canalData}
                                        dataKey="value"
                                        nameKey="name"
                                        cx="50%"
                                        cy="42%"
                                        innerRadius={50}
                                        outerRadius={85}
                                        paddingAngle={3}
                                    >
                                        {canalData.map((_, i) => (
                                            <Cell key={i} fill={CANAL_COLORS[i % CANAL_COLORS.length]} stroke={colorMode === 'dark' ? '#1A202C' : '#FFFFFF'} strokeWidth={2} />
                                        ))}
                                    </Pie>
                                    <Tooltip
                                        content={({ active, payload }) => {
                                            if (active && payload && payload.length) {
                                                const data = payload[0];
                                                const total = canalData.reduce((acc, c) => acc + c.value, 0);
                                                const pct = total > 0 ? Math.round((data.value / total) * 100) : 0;
                                                return (
                                                    <Box
                                                        bg={colorMode === 'dark' ? '#1A202C' : '#FFFFFF'}
                                                        border="1px solid"
                                                        borderColor={colorMode === 'dark' ? '#2D3748' : '#E5E7EB'}
                                                        borderRadius="lg"
                                                        p={3}
                                                        boxShadow="xl"
                                                    >
                                                        <HStack spacing={2}>
                                                            <Box w="8px" h="8px" borderRadius="full" bg={data.color} />
                                                            <Text fontSize="xs" fontWeight="800" color="textPrimary">{data.name}</Text>
                                                        </HStack>
                                                        <Text fontSize="xs" color="textSecondary" mt={1}>
                                                            {data.value} negócio(s) ({pct}%)
                                                        </Text>
                                                    </Box>
                                                );
                                            }
                                            return null;
                                        }}
                                    />
                                    <Legend
                                        verticalAlign="bottom"
                                        height={45}
                                        iconType="circle"
                                        formatter={(value) => (
                                            <Text as="span" fontSize="11px" color={colorMode === 'dark' ? '#E2E8F0' : '#2D3748'} fontWeight="700">
                                                {value}
                                            </Text>
                                        )}
                                    />
                                </PieChart>
                            </ResponsiveContainer>
                        )}
                    </Box>
                </Panel>
            </Grid>

            <Grid templateColumns={{ base: '1fr', lg: '1fr 1fr' }} gap={5}>
                {/* Fechamentos próximos */}
                <Panel title="Fechamentos nos Próximos 14 Dias" action={
                    <Badge bg="rgba(2,234,117,0.15)" color="secondary" fontSize="xs">
                        {proximosVencimentos.length} negócios
                    </Badge>
                }>
                    <VStack align="stretch" spacing={2} maxH="260px" overflowY="auto">
                        {proximosVencimentos.length === 0 && (
                            <Text color="textMuted" fontSize="sm" textAlign="center" py={10}>
                                Nenhum fechamento previsto para os próximos 14 dias
                            </Text>
                        )}
                        {proximosVencimentos.map(n => {
                            const dias = diasAte(n.dataFechamento);
                            return (
                                <HStack
                                    key={n.id}
                                    bg={colorMode === 'dark' ? "whiteAlpha.50" : "blackAlpha.50"}
                                    px={3}
                                    py={2}
                                    borderRadius="md"
                                    border="1px solid"
                                    borderColor="borderPrimary"
                                    justify="space-between"
                                >
                                    <VStack align="start" spacing={0} flex={1} minW={0}>
                                        <Text fontSize="sm" fontWeight="800" color="textPrimary" isTruncated maxW="100%">
                                            {n.titulo}
                                        </Text>
                                        <Text fontSize="xs" color="textSecondary">{n.empresa}</Text>
                                    </VStack>
                                    <VStack align="end" spacing={0} flexShrink={0}>
                                        <Text fontWeight="900" fontSize="sm" color="secondary">
                                            {fmt(n.valor)}
                                        </Text>
                                        <Badge
                                            fontSize="xs"
                                            bg={dias <= 3 ? 'rgba(252,129,129,0.2)' : 'rgba(236,201,75,0.15)'}
                                            color={dias <= 3 ? '#FC8181' : '#ECC94B'}
                                        >
                                            <HStack spacing={1}>
                                                <Icon as={AiOutlineCalendar} boxSize="10px" />
                                                <Text>{dias === 0 ? 'Hoje' : `${dias}d`}</Text>
                                            </HStack>
                                        </Badge>
                                    </VStack>
                                </HStack>
                            );
                        })}
                    </VStack>
                </Panel>

                {/* Atividades pendentes */}
                <Panel title="Atividades Pendentes" action={
                    <Badge bg="rgba(237,137,54,0.15)" color="orange.300" fontSize="xs">
                        {atividadesPendentes.length} tarefas
                    </Badge>
                }>
                    <VStack align="stretch" spacing={2} maxH="260px" overflowY="auto">
                        {atividadesPendentes.length === 0 && (
                            <Text color="textMuted" fontSize="sm" textAlign="center" py={10}>
                                Nenhuma atividade pendente
                            </Text>
                        )}
                        {atividadesPendentes.map(atv => {
                            const dataRef = atv.dataFim || atv.dataInicio || atv.dataVencimento;
                            const dias = diasAte(dataRef);
                            const atrasado = dias !== null && dias < 0;
                            return (
                                <HStack
                                    key={atv.id}
                                    bg={colorMode === 'dark' ? "whiteAlpha.50" : "blackAlpha.50"}
                                    px={3}
                                    py={2}
                                    borderRadius="md"
                                    border="1px solid"
                                    borderColor={atrasado ? 'red.900' : 'borderPrimary'}
                                    justify="space-between"
                                >
                                    <VStack align="start" spacing={0} flex={1} minW={0}>
                                        <Text fontSize="sm" fontWeight="700" color="textPrimary" isTruncated maxW="100%">
                                            {atv.nome || atv.descricao}
                                        </Text>
                                        <Text fontSize="xs" color="textSecondary">{atv.tipo || 'Atividade'} {atv.empresa ? `· ${atv.empresa}` : ''}</Text>
                                    </VStack>
                                    <Badge
                                        fontSize="xs"
                                        bg={atrasado ? 'rgba(229,62,62,0.2)' : 'rgba(66,153,225,0.15)'}
                                        color={atrasado ? 'red.300' : 'blue.300'}
                                        flexShrink={0}
                                    >
                                        {atrasado ? `${Math.abs(dias)}d atraso` : dias === 0 ? 'Hoje' : `${dias}d`}
                                    </Badge>
                                </HStack>
                            );
                        })}
                    </VStack>
                </Panel>
            </Grid>

            {/* Resumo por etapa */}
            <Panel title="Resumo do Funil — Valor por Etapa">
                <Box overflowX="auto">
                    <Table variant="simple" size="sm">
                        <Thead>
                            <Tr>
                                <Th color="textSecondary" borderColor="borderPrimary">Etapa</Th>
                                <Th color="textSecondary" borderColor="borderPrimary" isNumeric>Negócios</Th>
                                <Th color="textSecondary" borderColor="borderPrimary" isNumeric>Valor Total</Th>
                                <Th color="textSecondary" borderColor="borderPrimary" isNumeric>Ticket Médio</Th>
                                <Th color="textSecondary" borderColor="borderPrimary" isNumeric>% do Pipeline</Th>
                            </Tr>
                        </Thead>
                        <Tbody>
                            {etapasTabela.map(etapa => {
                                const lista = negocios.filter(n => contaNoFunil(n, etapa));
                                const total = lista.reduce((acc, n) => acc + (Number(n.valor) || 0), 0);
                                const ticket = lista.length > 0 ? Math.round(total / lista.length) : 0;
                                const pct = indicadores.totalPipeline > 0 && etapa.tipo !== 'ganho' && etapa.tipo !== 'perdido' && etapa.id !== 'ganho' && etapa.id !== 'perdido'
                                    ? Math.round((total / indicadores.totalPipeline) * 100)
                                    : null;
                                return (
                                    <Tr key={etapa.id} _hover={{ bg: colorMode === 'dark' ? 'whiteAlpha.50' : 'blackAlpha.50' }}>
                                        <Td borderColor="borderPrimary">
                                            <HStack spacing={2}>
                                                <Box w="8px" h="8px" borderRadius="full" bg={etapa.cor} flexShrink={0} />
                                                <Text fontSize="sm" fontWeight="700" color="textPrimary">{etapa.label}</Text>
                                            </HStack>
                                        </Td>
                                        <Td borderColor="borderPrimary" isNumeric>
                                            <Badge
                                                bg={colorMode === 'dark' ? `${etapa.cor}20` : `${etapa.cor}15`}
                                                color={colorMode === 'dark' ? etapa.cor : (
                                                    etapa.tipo === 'ganho' || etapa.id === 'ganho' ? 'green.600' :
                                                    etapa.id === 'qualificacao' ? 'yellow.600' :
                                                    etapa.id === 'negociacao' ? 'red.600' :
                                                    etapa.cor
                                                )}
                                                fontWeight="800"
                                            >
                                                {lista.length}
                                            </Badge>
                                        </Td>
                                        <Td borderColor="borderPrimary" isNumeric>
                                            <Text fontSize="sm" fontWeight="700" color={etapa.tipo === 'ganho' || etapa.id === 'ganho' ? (colorMode === 'dark' ? 'secondary' : 'green.600') : 'textPrimary'}>
                                                {fmt(total)}
                                            </Text>
                                        </Td>
                                        <Td borderColor="borderPrimary" isNumeric>
                                            <Text fontSize="sm" color="textSecondary">{ticket > 0 ? fmt(ticket) : '—'}</Text>
                                        </Td>
                                        <Td borderColor="borderPrimary" isNumeric>
                                            {pct !== null ? (
                                                <HStack justify="flex-end" spacing={2}>
                                                    <Text fontSize="xs" color="textSecondary">{pct}%</Text>
                                                    <Box w="60px">
                                                        <Progress value={pct} size="xs" colorScheme="green" borderRadius="full" bg={colorMode === 'dark' ? "gray.700" : "gray.200"} />
                                                    </Box>
                                                </HStack>
                                            ) : (
                                                <Text fontSize="xs" color="textMuted">—</Text>
                                            )}
                                        </Td>
                                    </Tr>
                                );
                            })}
                        </Tbody>
                    </Table>
                </Box>
            </Panel>
        </VStack>
    );
};

export default ComercialDashboard;
